import intersect from 'intersect';
import consolv from 'lib/consolv';
import waitForDependency from 'lib/waitForDependency';
// import buildSizeMap from 'lib/buildSizeMap';

const defaultConfig = {
  enabled: false,
  prebidTimeout: 1000,
  failsafeTimeout: 2000,
};

/**
 *  Waits for the ATS library to load, then initializes it
 */
function setupATS(MCP, configuration) {
  return waitForDependency(() => window.ats)
    .then((ats) => {
      if (!configuration) throw new Error('No configuration');
      console.log(ats.start(configuration));
      return ats.retrieveEnvelope();
    })
    .then((envelope) => {
      if (envelope === undefined) throw new Error('No envelope');
      MCP.setPageTargeting({ has_ats: '1' });
    })
    .catch(() => {
      MCP.setPageTargeting({ has_ats: '0' });
    });
}

function generateFirstPartyData(meta) {
  const data = {
    topic: [],
  };

  if (meta.page.topic) data.topic.push(meta.page.topic);
  return data;
}

/**
 * Creates the prebid request and callback that is called before loading slots
 * @param {MCPInstance} MCP
 * @param {Object} config
 */
const setupRequestBids = (MCP, config) => (event, { slots, addAsyncEvent, immediatelyLoad }) => {
  if (event === 'beforeAppendSlot' && !immediatelyLoad) return;

  const configuration = MCP.Configurator.getConfiguration();

  const runPerformanceTest = MCP._config.performanceTest === true;
  let startTime;
  if (runPerformanceTest) startTime = window.performance.now();

  const asyncEvent = new Promise((resolve) => {
    if (!window.pbjs || !window.pbjs.que) { window.pbjs = { ...window.pbjs, que: [] }; }
    const { pbjs } = window;
    // const pbjs = loadPrebidLibrary();
    let failsafeTimerRef;

    /* eslint-disable-next-line prefer-arrow-callback */
    pbjs.que.push(function requestBids() {
      const adUnits = Array.from(slots).reduce((acc, [element, slot]) => {
        const { site } = MCP._sourceOfTruth.page;
        const slotType = slot.meta.bidAs || slot.type;
        const slotMap = config.slotMap &&
          config.slotMap[site] &&
          config.slotMap[site][slotType];

        if (!slotMap) return acc;

        const bids = Object.keys(slotMap).reduce((bidsAcc, bidId) => {
          // Check to see if bid has a specific size restriction and compare
          const { sizes: slotSizes, bidder, params } = slotMap[bidId];
          const bidSizes = slot.sizes.map(v => v.join(','));
          const expandedParams = { ...params };

          // Fix for Pubmatic not being able to simply look at the valid sizes in the request
          // Filters out any 1xX sizes
          if (bidder === 'pubmatic' && expandedParams.multipleAdSizes !== undefined) {
            expandedParams.multipleAdSizes = slot.sizes.filter(v => v[0] > 1);
          }

          if (!slotMap[bidId].sizes || intersect(slotSizes, bidSizes).length) {
            bidsAcc.push({
              bidder: bidder || bidId,
              params: expandedParams,
            });
          }

          return bidsAcc;
        }, []);

        const filteredSizes = slot.sizes.filter(size => size.every(value => value !== Infinity));
        const slotDefinition = configuration.slotDefinitions[slotType];
        const { mediaTypes: supportedTypes = ['banner'] } = slotDefinition;

        const bidObject = {
          code: element.id,
          mediaTypes: {},
          bids,
        };

        supportedTypes.forEach((supportedType) => {
          switch (supportedType) {
            case 'native': {
              bidObject.mediaTypes.native = {
                ...slotDefinition.nativeFields,
              };
              break;
            }

            case 'banner': {
              bidObject.mediaTypes.banner = {
                sizes: filteredSizes,
              };
              break;
            }

            case 'video': {
              bidObject.mediaTypes.video = {
                ...slotDefinition.videoFields,
              };
              break;
            }

            default:
          }
        });

        acc.push(bidObject);
        return acc;
      }, []);

      if (!adUnits.length) {
        consolv.log('MCP: Prebid has no valid ad units');
        resolve();
        return;
      }

      const bidRequest = {
        adUnitCodes: adUnits.map(adUnit => adUnit.code),
        bidsBackHandler: (bids) => {
          pbjs.setTargetingForGPTAsync();
          window.clearTimeout(failsafeTimerRef);

          if (runPerformanceTest) {
            const requestDuration = (window.performance.now() - startTime) / 1000;
            consolv.log('MCP: Prebid requests received', bids, `${requestDuration} seconds`);
          } else {
            consolv.log('MCP: Prebid requests received', bids);
          }

          /*
          // Find bids on native slots
          const nativeSlots = Object.keys(bids)
            .filter(v => MCP.slots.get(document.getElementById(v)).type === 'dfp_native')
            .map(v => bids[v]);

          if (nativeSlots.length) {
            nativeSlots.forEach((slotBids) => {
              const highestBid = slotBids.bids.reduce((acc, curr) => {
                if (!acc) return curr;
                if (curr.cpm > acc.cpm) return curr;
                return acc;
              });

              if (highestBid.bidderCode === 'triplelift') {
                const slotDefinition = configuration.slotDefinitions['dfp_native'];
                if (slotDefinition) {
                  const defaultMap = slotDefinition.sizes.default;
                  const filteredMap = defaultMap.filter(v => v !== '1,11');
                  const builtMap =
                    googletag.sizeMapping().addSize([0, 0], buildSizeMap(filteredMap)).build();

                  const slot = MCP.slots.get(document.getElementById(highestBid.adUnitCode));
                  slot._DFPSlot.defineSizeMapping(map);
                }
              }
            });
          }
          console.log('native', nativeSlots);
          */

          resolve();
        },
        timeout: config.prebidTimeout,
      };

      pbjs.addAdUnits(adUnits);
      pbjs.requestBids(bidRequest);
      consolv.log('MCP: Prebid Request Bids', adUnits);
    });

    failsafeTimerRef = window.setTimeout(() => {
      consolv.log(`MCP: Prebid failsafe timeout exceeded at ${config.prebidTimeout} seconds`);
      resolve();
    }, config.failsafeTimeout);
  });

  addAsyncEvent(asyncEvent);
};

/**
 * Generates the configuration and sets up event listeners for slot loads
 */
export default function PrebidAdapter(MCP) {
  if (!window.pbjs || !window.pbjs.que) { window.pbjs = { ...window.pbjs, que: [] }; }
  const { pbjs } = window;

  MCP.loaded.then(() => {
    const config = MCP.Configurator.getConfiguration();
    const prebidConfig = { ...defaultConfig, ...config.prebid };
    const { priceGranularity, config: additionalConfig, analytics } = prebidConfig;

    if (prebidConfig.atsEnabled) {
      setupATS(MCP, prebidConfig.atsConfig);
    }

    if (prebidConfig.enabled) {
      const requestBids = setupRequestBids(MCP, prebidConfig);

      if (priceGranularity || additionalConfig || analytics) {
        /* eslint-disable-next-line prefer-arrow-callback */
        pbjs.que.push(function setPriceGranularity() {
          if (priceGranularity) {
            pbjs.setConfig({ priceGranularity });
          }

          if (config) {
            pbjs.setConfig(additionalConfig);
          }

          if (analytics) {
            Object.keys(analytics).forEach((provider) => {
              pbjs.enableAnalytics({ provider, ...analytics[provider] });
            });
          }

          const firstPartyData = {
            fpd: {
              context: generateFirstPartyData(MCP.Configurator.meta) || {},
            },
          };
          pbjs.setConfig(firstPartyData);
        });
      }

      MCP.addEventListener('beforeLoadSlots',
        (...params) => requestBids('beforeLoadSlots', ...params));
      MCP.addEventListener('beforeAppendSlot',
        (...params) => requestBids('beforeAppendSlot', ...params));
      MCP.addEventListener('beforeRefreshSlots',
        (...params) => requestBids('beforeRefreshSlots', ...params));
    }
  });
}
